import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { format, parseISO } from "date-fns";
import { 
  UserCheck, 
  Loader2, 
  Calendar,
  Clock,
  User,
  Mail,
  Phone,
  CheckCircle,
  XCircle
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminPrivateSessions = () => {
  const [sessions, setSessions] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== "all" ? { status: statusFilter } : {};
      const [sessionsRes, instructorsRes] = await Promise.all([
        axios.get(`${API}/admin/private-sessions`, { params }),
        axios.get(`${API}/instructors`),
      ]);
      setSessions(sessionsRes.data.sessions || []);
      setInstructors(instructorsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = (sessionId, newStatus) => {
    if (newStatus === "cancelled") {
      toast.warning("Decline this session request?", {
        description: "The request will be marked as cancelled.",
        action: {
          label: "Decline",
          onClick: async () => {
            try {
              await axios.put(`${API}/admin/private-sessions/${sessionId}`, { status: "cancelled" });
              toast.success("Session declined");
              fetchData();
              setSelectedSession(null);
            } catch {
              toast.error("Failed to update session");
            }
          },
        },
        cancel: { label: "Keep", onClick: () => {} },
      });
      return;
    }
    const SESSION_STATUS_LABELS = { confirmed: "Session confirmed", completed: "Session marked complete" };
    axios.put(`${API}/admin/private-sessions/${sessionId}`, { status: newStatus })
      .then(() => { toast.success(SESSION_STATUS_LABELS[newStatus] || `Session ${newStatus}`); fetchData(); setSelectedSession(null); })
      .catch(() => toast.error("Failed to update session"));
  };

  const handleAssignInstructor = async (sessionId, instructorId) => {
    try {
      await axios.put(`${API}/admin/private-sessions/${sessionId}`, { instructor_id: instructorId });
      toast.success("Instructor assigned");
      fetchData();
    } catch (error) {
      toast.error("Failed to assign instructor");
    }
  };

  const statusColors = {
    pending: "bg-yellow-500/20 text-yellow-400",
    confirmed: "bg-green-500/20 text-green-400",
    completed: "bg-blue-500/20 text-blue-400",
    cancelled: "bg-red-500/20 text-red-400",
  };

  const sessionTypeLabels = {
    one_on_one: "1-on-1",
    couple: "Couple",
    small_group: "Small Group",
  };

  return (
    <div data-testid="admin-private-sessions">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 md:mb-8 gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white">Private Sessions</h1>
          <p className="text-neutral-400">Manage private session requests</p>
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] bg-neutral-900 border-neutral-800">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        <Card className="card-empire">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-white">{sessions.length}</div>
            <p className="text-sm text-neutral-400">Total Requests</p>
          </CardContent>
        </Card>
        <Card className="card-empire">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-yellow-400">
              {sessions.filter(s => s.status === "pending").length}
            </div>
            <p className="text-sm text-neutral-400">Pending</p>
          </CardContent>
        </Card>
        <Card className="card-empire">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-400">
              {sessions.filter(s => s.status === "confirmed").length}
            </div>
            <p className="text-sm text-neutral-400">Confirmed</p>
          </CardContent>
        </Card>
        <Card className="card-empire">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-empire-gold">
              KES {sessions.reduce((sum, s) => sum + (s.price || 0), 0).toLocaleString()}
            </div>
            <p className="text-sm text-neutral-400">Total Value</p>
          </CardContent>
        </Card>
      </div>

      {/* Sessions List */}
      <Card className="card-empire">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-empire-gold" />
            Session Requests
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-empire-gold animate-spin" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-12 text-neutral-500">
              <UserCheck className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No private session requests</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sessions.map((session, index) => (
                <motion.div
                  key={session.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex flex-col lg:flex-row lg:items-center justify-between p-4 bg-neutral-900/50 rounded-xl border border-neutral-800 gap-4"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-empire-gold/20 flex items-center justify-center flex-shrink-0">
                      <User className="w-6 h-6 text-empire-gold" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-white">{session.guest_name}</h4>
                        <Badge className={statusColors[session.status]}>
                          {session.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-neutral-400 mb-1">
                        {session.guest_email} • {session.guest_phone}
                      </p>
                      <div className="flex flex-wrap gap-3 text-sm text-neutral-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {session.preferred_date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {session.preferred_time}
                        </span>
                        <Badge variant="outline" className="border-neutral-700">
                          {sessionTypeLabels[session.session_type] || session.session_type}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 pl-16 lg:pl-0">
                    <div className="text-right">
                      <p className="text-xs text-neutral-500">Price</p>
                      <p className="font-bold text-empire-gold">KES {session.price?.toLocaleString()}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedSession(session)}
                      className="border-neutral-700"
                    >
                      Manage
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Session Detail Dialog */}
      <Dialog open={!!selectedSession} onOpenChange={() => setSelectedSession(null)}>
        <DialogContent className="bg-neutral-900 border-neutral-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Session Details</DialogTitle>
          </DialogHeader>
          {selectedSession && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-neutral-500">Reference</p>
                  <p className="font-mono text-empire-gold">{selectedSession.session_number}</p>
                </div>
                <div>
                  <p className="text-sm text-neutral-500">Status</p>
                  <Badge className={statusColors[selectedSession.status]}>
                    {selectedSession.status}
                  </Badge>
                </div>
              </div>

              <div>
                <p className="text-sm text-neutral-500">Guest</p>
                <p className="text-white font-medium">{selectedSession.guest_name}</p>
                <p className="text-neutral-400 text-sm">{selectedSession.guest_email}</p>
                <p className="text-neutral-400 text-sm">{selectedSession.guest_phone}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-neutral-500">Date & Time</p>
                  <p className="text-white">{selectedSession.preferred_date}</p>
                  <p className="text-neutral-400 text-sm">{selectedSession.preferred_time}</p>
                </div>
                <div>
                  <p className="text-sm text-neutral-500">Duration</p>
                  <p className="text-white">{selectedSession.duration_minutes} minutes</p>
                </div>
              </div>

              <div>
                <p className="text-sm text-neutral-500">Session Type</p>
                <p className="text-white">{sessionTypeLabels[selectedSession.session_type]} - {selectedSession.dance_style || "Any style"}</p>
              </div>

              {selectedSession.goals && (
                <div>
                  <p className="text-sm text-neutral-500">Goals</p>
                  <p className="text-white text-sm">{selectedSession.goals}</p>
                </div>
              )}

              <div>
                <p className="text-sm text-neutral-500 mb-2">Assign Instructor</p>
                <Select
                  value={selectedSession.instructor_id || ""}
                  onValueChange={(value) => handleAssignInstructor(selectedSession.id, value)}
                >
                  <SelectTrigger className="bg-neutral-800 border-neutral-700">
                    <SelectValue placeholder="Select instructor" />
                  </SelectTrigger>
                  <SelectContent>
                    {instructors.map((instructor) => (
                      <SelectItem key={instructor.id} value={instructor.id}>
                        {instructor.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-neutral-800">
                <span className="text-neutral-400">Price</span>
                <span className="text-2xl font-bold text-empire-gold">
                  KES {selectedSession.price?.toLocaleString()}
                </span>
              </div>

              {selectedSession.status === "pending" && (
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleUpdateStatus(selectedSession.id, "cancelled")}
                    variant="outline"
                    className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Decline
                  </Button>
                  <Button
                    onClick={() => handleUpdateStatus(selectedSession.id, "confirmed")}
                    className="flex-1 btn-empire"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Confirm
                  </Button>
                </div>
              )}

              {selectedSession.status === "confirmed" && (
                <Button
                  onClick={() => handleUpdateStatus(selectedSession.id, "completed")}
                  className="w-full btn-empire"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Mark as Completed
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
